<script setup>
    import { useI18n } from 'vue-i18n';
    import { computed, unref } from 'vue';
    import forEach from "lodash/forEach";
    import DownloadLink from "./DownloadLink.vue";
    import SecurePopup from "./SecurePopup.vue";
    import useLiferayLocale from "../../../composables/useLiferayLocale";

    const { t } = useI18n();
    const {cldrLocale} = useLiferayLocale();

    const props = defineProps({
        downloadAssets: {
            type: Object,
            required: true,
        },

        index: {
            type: Number,
            required: true,
        },

        productDetails: {
            type: Boolean,
            required: true,
            default: false,
        },
    });

    const titleClass = computed(() => {
        if(props.downloadAssets.visibility !== null && props.downloadAssets.visibility !== ""
            && (props.downloadAssets.visibility === "Distributor" || props.downloadAssets.visibility === "request-access" || props.downloadAssets.visibility === "Internal")) {
            return 'is-grid';
        } else {
            return 'is-flex';
        }
    });

    const hasIcon = computed(() => {
        return props.downloadAssets.iconType !== null && props.downloadAssets.iconType !== "";
    });

    function getCurrentIcon() {
        let currentIcon = props.downloadAssets.iconType;

        if(currentIcon === 'eprt' || currentIcon === 'step' || currentIcon === 'stp' ||  currentIcon === 'ldt'){
            currentIcon = "generic";
        } else if(currentIcon === 'ies' || currentIcon === 'apk') {
            currentIcon = "code";
        }

        if(props.downloadAssets.visibility !== null && props.downloadAssets.visibility !== ""
            && (props.downloadAssets.visibility === "Distributor" || props.downloadAssets.visibility === "request-access" || props.downloadAssets.visibility === "Internal")) {
            if(currentIcon === 'tif' || currentIcon === 'png' || currentIcon === 'jpg' ||  currentIcon === 'jpeg') {
                currentIcon = "image";
            }
            currentIcon = currentIcon + "-locked"
        }

        return currentIcon;
    }

    const currentIconHref = computed(() => {
        return "#icon-" + getCurrentIcon();
    });

    const currentIconClass = computed(() => {
        return "icon-download-" + getCurrentIcon();
    });

    const getLinkHref = computed(() => {
        if(props.downloadAssets.visibility !== null && props.downloadAssets.visibility !== ""
            && (props.downloadAssets.visibility === "request-access" || props.downloadAssets.visibility === "Distributor" || props.downloadAssets.visibility === "Internal")){
            return "#";
        } else {
            let link = "";
            if(props.productDetails) {
                if(props.downloadAssets.files.length  === 1 || props.downloadAssets.files.length <= 1){
                    if(props.downloadAssets.files[0].url !== null && props.downloadAssets.files[0].url !== "") {
                        link = props.downloadAssets.files[0].url;
                    }
                } else {
                    let isSameAsLocale = false;
                    let isSameAsFallback = false;
                    let sameAsLocaleDownloadLink = "";
                    let firstDownloadLink = "";
                    let fallbackDownloadLink = "";
                    forEach(props.downloadAssets.files, links => {
                        if(links.language !== null) {
                            if(links.language === unref(cldrLocale)) {
                                isSameAsLocale = true;
                                sameAsLocaleDownloadLink = links.url;
                            } else if(links.language === "en_US") {
                                isSameAsFallback = true;
                                fallbackDownloadLink = links.url;
                            } else if(links.language !== "") {
                                firstDownloadLink = links.url;
                            }
                        } else {
                            firstDownloadLink = links.url;
                        }
                    });

                    if(isSameAsLocale && sameAsLocaleDownloadLink !== "") {
                        link = sameAsLocaleDownloadLink;
                    } else if(isSameAsFallback && fallbackDownloadLink !== "") {
                        link = fallbackDownloadLink;
                    } else if(!isSameAsLocale && !isSameAsFallback && firstDownloadLink !== "") {
                        link = firstDownloadLink;
                    }
                }
            }
            return link;
        }
    });

    const dataPopupId = computed(() => {
        if(props.downloadAssets.visibility !== null && props.downloadAssets.visibility !== ""
            && (props.downloadAssets.visibility === "request-access" || props.downloadAssets.visibility === "Distributor" || props.downloadAssets.visibility === "Internal")){
            let id = "";
            if(props.productDetails) {
                if(props.downloadAssets.files.length  === 1 || props.downloadAssets.files.length <= 1){
                    if(props.downloadAssets.files[0].id !== null && props.downloadAssets.files[0].id !== "") {
                        id = props.downloadAssets.files[0].id;
                    }
                } else {
                    let isSameAsLocale = false;
                    let isSameAsFallback = false;
                    let sameAsLocaleDownloadId = "";
                    let firstDownloadId = "";
                    let fallbackDownloadId = "";
                    forEach(props.downloadAssets.files, links => {
                        if(links.language !== null) {
                            if(links.language === unref(cldrLocale)) {
                                isSameAsLocale = true;
                                sameAsLocaleDownloadId = links.id;
                            } else if(links.language === "en_US") {
                                isSameAsFallback = true;
                                fallbackDownloadId = links.id;
                            } else if(links.language !== "") {
                                firstDownloadId = links.id;
                            }
                        } else {
                            firstDownloadId = links.id;
                        }
                    });

                    if(isSameAsLocale && sameAsLocaleDownloadId !== "") {
                        id = sameAsLocaleDownloadId;
                    } else if(isSameAsFallback && fallbackDownloadId !== "") {
                        id = fallbackDownloadId;
                    } else if(!isSameAsLocale && !isSameAsFallback && firstDownloadId !== "") {
                        id = firstDownloadId;
                    }
                }
            }
            return id;
        }
        return "";
    });

    const downloadContainerClass = computed(() => {
        if(props.downloadAssets.visibility !== null && props.downloadAssets.visibility !== "") {
            if(props.downloadAssets.visibility === "Distributor") {
                return 'has-disti-files';
            } else if(props.downloadAssets.visibility === "request-access") {
                return 'has-request-access-files';
            } else if(props.downloadAssets.visibility === "Internal") {
                return 'has-internal-files has-disti-files';
            }
        }
        return "";
    });

    const showDistiDialog = computed(() => {
        if(props.downloadAssets.visibility !== null && props.downloadAssets.visibility !== "") {
            if(props.downloadAssets.visibility === "Distributor") {
                return true;
            }
        }
        return false;
    });

    const showSecurityDialog = computed(() => {
        if(props.downloadAssets.visibility !== null && props.downloadAssets.visibility !== "") {
            if(props.downloadAssets.visibility === "request-access") {
                return true;
            }
        }
        return false;
    });

    const showInternalDialog = computed(() => {
        if(props.downloadAssets.visibility !== null && props.downloadAssets.visibility !== "") {
            if(props.downloadAssets.visibility === "Internal") {
                return true;
            }
        }
        return false;
    });

    const linkClass = computed(() => {
        let classText = "";
        if(props.downloadAssets.files.length > 1) {
            forEach(props.downloadAssets.files, links => {
                if(links.description !== null && links.description !== "" && links.language !== null && links.language !== "" && links.language === unref(cldrLocale)){
                    if(links.description !== props.downloadAssets.name) {
                        classText = "tooltip";
                    }
                }
            });
        } else {
            forEach(props.downloadAssets.files, links => {
                if(links.description !== null && links.description !== ""){
                    if(links.description !== props.downloadAssets.name) {
                        classText = "tooltip";
                    }
                }
            });
        }
         return classText;
    });

    const linkDescription = computed(() => {
        let description = "";
        if(props.downloadAssets.files.length > 1) {
            forEach(props.downloadAssets.files, links => {
                if(links.description !== null && links.description !== "" && links.language !== null && links.language !== "" && links.language === unref(cldrLocale)){
                    if(links.description !== props.downloadAssets.name) {
                        description =  links.description;
                    }
                }
            });
        } else {
            forEach(props.downloadAssets.files, links => {
                if(links.description !== null && links.description !== ""){
                    if(links.description !== props.downloadAssets.name) {
                        description =  links.description;
                    }
                }
            });
        }
        return description;
    });
</script>

<template>
    <div class="download-container " :class="downloadContainerClass">
        <div class="left-content">
            <h4 class="download-title title is-4 " :class="titleClass">
                <svg v-if="hasIcon" class="icon " :class="currentIconClass">
                    <use :xlink:href="currentIconHref"/>
                </svg>

                <a
                    v-if="getLinkHref !== '#'"
                    :href="getLinkHref"
                    :data-popup-id="dataPopupId"
                    target="_blank"
                >
                    <span v-if="downloadAssets.name !== null && downloadAssets.name !== ''" :class="linkClass" :data-title="linkDescription">
                        {{ downloadAssets.name }}
                    </span>
                </a>

                <span v-if="getLinkHref === '#' && downloadAssets.name !== null && downloadAssets.name !== ''" :class="linkClass" :data-title="linkDescription">
                    {{ downloadAssets.name }}
                </span>

                <span v-if="showDistiDialog" class="box-info disti tooltip" :data-title="t('productDetails.Download.Disti.Information.Tooltip')">
                    {{ t('productDetails.Download.Disti.Information.Text') }}
                </span>

                <span v-if="showSecurityDialog" class="box-info security tooltip" :data-title="t('productDetails.Download.Secure.Information.Tooltip')">
                    <svg class="icon icon-secure">
                        <use xlink:href="#secure-file"/>
                    </svg>
                   {{ t('productDetails.Download.Secure.Information.Text') }}
               </span>

                <span v-if="showInternalDialog" class="box-info internal tooltip" :data-title="t('downloadCenter_internalTooltip')">
                    {{ t('downloadCenter_internalText') }}
                </span>
            </h4>
        </div>
        <div class="right-content">
            <div v-if="downloadAssets.type !== null && downloadAssets.type !== ''" class="download-type">
                {{ downloadAssets.type }}
            </div>
            <div v-if="downloadAssets.files !== null && downloadAssets.files.length > 0" class="links-container">
                <DownloadLink
                    v-for="(links, index) in downloadAssets.files"
                    :links="links"
                    :index="index"
                    :has-icon="(downloadAssets.files.length  === 1 || downloadAssets.files.length <= 1)"
                    :show-security-dialog="showSecurityDialog"
                    :show-disti-internal-dialog="showDistiDialog || showInternalDialog"
                    :asset-name="downloadAssets.name"
                    :asset-type="downloadAssets.type"
                    :visibility="downloadAssets.visibility"
                ></DownloadLink>
            </div>
        </div>
        <SecurePopup
            v-if="showSecurityDialog"
            v-for="(links, index) in downloadAssets.files"
            :links="links"
            :index="index"></SecurePopup>
    </div>
</template>
